"use client";

import { CheckIcon, XIcon } from "@phosphor-icons/react";
import { DateTime } from "luxon";
import { useState } from "react";

import { formatDateTimeDisplay } from "@/packages/shared/src/utils/date-utils";
import { SchedulingEligibility } from "@/src/components/comms/scheduling-eligibility";
import { Button } from "@/src/components/shadcn/ui/button";
import { Checkbox } from "@/src/components/shadcn/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/shadcn/ui/popover";
import { Textarea } from "@/src/components/shadcn/ui/textarea";
import { useUser } from "@/src/context/user-context";
import { useSaveButtonShortcut } from "@/src/hooks/use-save-button-shortcut";
import {
  useReferralSchedulingRecommendation,
  useVerifySchedulingRecommendation,
} from "@/src/lib/data-fetchers/scheduling-fetchers";
import { cn } from "@/src/lib/shadcn-utils";
import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { Referral } from "@/src/models/documents/referral";
import {
  SCHEDULING_VERIFICATION_REASON_CODES,
  SCHEDULING_VERIFICATION_REASON_LABELS,
  SchedulingRecommendation,
  SchedulingVerificationReasonCode,
  VerificationReason,
  shouldRecommendForSchedulingInReferralTask,
} from "@/src/models/scheduling-recommendation";

interface ReferralSchedulingRecommendationProps {
  referral: Referral;
  title?: string;
}

export function ReferralSchedulingRecommendation({
  referral,
  title = "Scheduling recommendation",
}: ReferralSchedulingRecommendationProps) {
  const { group } = useUser();
  const {
    recommendation: referralSchedulingRecommendation,
    isPending: isReferralSchedulingRecommendationPending,
    isError,
  } = useReferralSchedulingRecommendation(referral?.id, true /* schedulingEnabled */);

  const shouldRecommendForSchedulingInReferralTaskBoolean = shouldRecommendForSchedulingInReferralTask(
    group,
    referralSchedulingRecommendation
  );

  if (!referral?.id) {
    return (
      <div className="mb-4 my-2">
        {title ? <h3 className="font-semibold text-foreground my-2">{title}</h3> : null}
        <span className="text-sm text-muted-foreground">No referral ID available</span>
      </div>
    );
  }

  return (
    <div className="mb-4 my-2">
      {title ? <h3 className="font-semibold text-foreground my-2">{title}</h3> : null}
      {isReferralSchedulingRecommendationPending ? (
        <span className="text-sm text-muted-foreground">Checking for eligibility...</span>
      ) : isError ? (
        <span className="text-sm text-muted-foreground">Unable to load recommendation</span>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">
              {shouldRecommendForSchedulingInReferralTaskBoolean ? "Recommended" : "Not Recommended"}
            </span>
            <SchedulingEligibility
              agenticRecommendation={referralSchedulingRecommendation?.agenticRecommendation}
              receivingProvider={referral.receivingProvider}
            />
          </div>
          <RecommendedSlotSection recommendation={referralSchedulingRecommendation} />
          <SchedulingRecommendationVerification
            referralId={referral.id}
            recommendation={referralSchedulingRecommendation}
          />
        </div>
      )}
    </div>
  );
}

interface RecommendedSlotSectionProps {
  recommendation: SchedulingRecommendation | null;
}

export function RecommendedSlotSection({ recommendation }: RecommendedSlotSectionProps) {
  if (recommendation?.recommendedAppointment) {
    return <RecommendedSlotSummary appointment={recommendation.recommendedAppointment} />;
  }

  if (recommendation?.noRecommendationReason) {
    return (
      <span className="text-sm text-muted-foreground">
        No appointment recommended: {recommendation.noRecommendationReason.explanation}
      </span>
    );
  }

  return null;
}

interface RecommendedSlotSummaryProps {
  appointment: NonNullable<SchedulingRecommendation["recommendedAppointment"]>;
}

function RecommendedSlotSummary({ appointment }: RecommendedSlotSummaryProps) {
  const { providerName, locationName, reasoning, appointmentSlot } = appointment;
  const endTime = DateTime.fromISO(appointmentSlot.end, { setZone: true }).toFormat("h:mm a");
  const formattedRange = `${formatDateTimeDisplay(appointmentSlot.start)} – ${endTime}`;

  return (
    <div className="flex flex-col gap-1 rounded-md border border-border bg-muted/40 p-2">
      <span className="text-sm font-medium text-foreground">Recommended appointment</span>
      <span className="text-sm text-foreground">
        {providerName ?? appointmentSlot.practitionerDisplayName ?? "Unknown provider"}
        {" at "}
        {locationName ?? appointmentSlot.locationName ?? "Unknown location"}
      </span>
      <span className="text-sm text-muted-foreground">{formattedRange}</span>
      <span className="text-sm text-muted-foreground">
        {formatFromUnderscoreToHumanReadable(appointmentSlot.appointmentType)}
      </span>
      {reasoning ? <span className="text-xs text-muted-foreground">{reasoning}</span> : null}
    </div>
  );
}

interface SchedulingRecommendationVerificationProps {
  referralId: string | null | undefined;
  recommendation: SchedulingRecommendation | null;
}

// Correct/Incorrect verification controls for a referral scheduling recommendation. Operates on an
// already-fetched recommendation so callers that have loaded it (e.g. the ENTP referral message)
// can reuse the buttons without triggering a second recommendation fetch.
export function SchedulingRecommendationVerification({
  referralId,
  recommendation,
}: SchedulingRecommendationVerificationProps) {
  const verifyMutation = useVerifySchedulingRecommendation(referralId);

  const isCorrect = recommendation?.isCorrect ?? null;
  const verificationReason = recommendation?.verificationReason ?? null;

  if (!recommendation) {
    return null;
  }

  return (
    <SchedulingVerificationControls
      isCorrect={isCorrect}
      verificationReason={verificationReason}
      isPending={verifyMutation.isPending}
      // Correct stays an instant toggle; clicking the active choice clears verification back to unverified.
      onMarkCorrect={() =>
        verifyMutation.mutate({ isCorrect: isCorrect === true ? null : true, verificationReason: null })
      }
      onMarkIncorrect={(reason) => verifyMutation.mutate({ isCorrect: false, verificationReason: reason })}
      onClear={() => verifyMutation.mutate({ isCorrect: null, verificationReason: null })}
    />
  );
}

interface SchedulingVerificationControlsProps {
  isCorrect: boolean | null;
  verificationReason: VerificationReason | null;
  isPending: boolean;
  onMarkCorrect: () => void;
  onMarkIncorrect: (reason: VerificationReason) => void;
  // Omit when clearing an existing verification isn't meaningful (e.g. the verification task, which
  // completes on the first choice); the Clear affordance is then hidden.
  onClear?: () => void;
}

// Presentational Correct/Incorrect controls, decoupled from how the verification is persisted. Both
// the inline referral verification and the standalone Scheduling Eligibility Verification task reuse
// this; callers supply the mutation behind the callbacks.
export function SchedulingVerificationControls({
  isCorrect,
  verificationReason,
  isPending,
  onMarkCorrect,
  onMarkIncorrect,
  onClear,
}: SchedulingVerificationControlsProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <Button
          type="button"
          size="sm"
          variant={isCorrect === true ? "default" : "outline"}
          disabled={isPending}
          onClick={onMarkCorrect}
          aria-pressed={isCorrect === true}
          className={cn("h-7 gap-1 px-2", isCorrect === true && "bg-success hover:bg-success/90")}
        >
          <CheckIcon className="h-3.5 w-3.5" weight="bold" />
          Correct
        </Button>
        <IncorrectReasonButton
          isActive={isCorrect === false}
          initialReason={verificationReason}
          isPending={isPending}
          onSubmit={onMarkIncorrect}
          onClear={onClear}
        />
      </div>
      {isCorrect === false && verificationReason ? (
        <span className="text-xs text-muted-foreground">
          {verificationReason.reasons.map((code) => SCHEDULING_VERIFICATION_REASON_LABELS[code]).join(", ")}
          {verificationReason.other ? `: ${verificationReason.other}` : ""}
        </span>
      ) : null}
    </div>
  );
}

interface IncorrectReasonButtonProps {
  isActive: boolean;
  initialReason: VerificationReason | null;
  isPending: boolean;
  onSubmit: (reason: VerificationReason) => void;
  onClear?: () => void;
}

// Marking a recommendation incorrect requires at least one reason, so the choice lives in a
// popover that only commits on Save (unlike the instant Correct toggle).
function IncorrectReasonButton({ isActive, initialReason, isPending, onSubmit, onClear }: IncorrectReasonButtonProps) {
  const [open, setOpen] = useState(false);
  const [reasons, setReasons] = useState<SchedulingVerificationReasonCode[]>([]);
  const [other, setOther] = useState("");
  const { handleDetailsKeyDown, saveButtonRef } = useSaveButtonShortcut();

  // Seed the form from the saved reason each time the popover opens.
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setReasons(initialReason?.reasons ?? []);
      setOther(initialReason?.other ?? "");
    }

    setOpen(next);
  };

  const toggleReason = (code: SchedulingVerificationReasonCode) => {
    setReasons((prev) => (prev.includes(code) ? prev.filter((value) => value !== code) : [...prev, code]));
  };

  const otherSelected = reasons.includes("other");
  const canSave = reasons.length > 0 && (!otherSelected || other.trim().length > 0);

  const handleSave = () => {
    onSubmit({ reasons, other: otherSelected ? other.trim() : null });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant={isActive ? "destructive" : "outline"}
          disabled={isPending}
          aria-pressed={isActive}
          className="h-7 gap-1 px-2"
        >
          <XIcon className="h-3.5 w-3.5" weight="bold" />
          Incorrect
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="start">
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-foreground">Why is this incorrect?</p>
          <div className="flex flex-col gap-1.5">
            {SCHEDULING_VERIFICATION_REASON_CODES.map((code) => (
              <label key={code} className="flex items-center gap-2 text-sm text-foreground">
                <Checkbox checked={reasons.includes(code)} onCheckedChange={() => toggleReason(code)} />
                {SCHEDULING_VERIFICATION_REASON_LABELS[code]}
              </label>
            ))}
          </div>
          {otherSelected ? (
            <Textarea
              value={other}
              onChange={(event) => setOther(event.target.value)}
              onKeyDown={handleDetailsKeyDown}
              placeholder="Describe the issue"
            />
          ) : null}
          <div className="flex items-center justify-end gap-2">
            {isActive && onClear ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  onClear();
                  setOpen(false);
                }}
                className="mr-auto h-7 px-2"
              >
                Clear
              </Button>
            ) : null}
            <Button
              ref={saveButtonRef}
              type="button"
              size="sm"
              disabled={!canSave || isPending}
              onClick={handleSave}
              className="h-7 px-2"
            >
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
